import { AdminUser } from 'tools/bff/generated/bff/admin/admin_user_pb';
import {
  Alert,
  Checkbox,
  Grid,
  Modal,
  PhoneInput,
  TagPicker,
  Text,
  TextInput,
} from '@tocktix/pantry';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { FunctionComponent, PropsWithChildren, useState } from 'react';
import { StoreType, useActions } from 'utils/connect';
import { formatRpcError } from 'utils/rpcError';
import { resolveUpdateUserError } from 'tools/users/updateUserErrors';
import { sortBy } from 'es-toolkit/compat';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import { userClient } from 'tools/bff/client';

export interface EditUserDetailsFormValues {
  phoneNumber: string;
  emailAddress?: string;
  isConcierge: boolean;
  conciergeGroups: string[];
}

interface EditUserDetailsModalProps {
  user: AdminUser;
  isOpen: boolean;
  onClose(): void;
  onSubmit(): Promise<void>;
}

const validate = (initialValues: EditUserDetailsFormValues, values: EditUserDetailsFormValues) => {
  const errors: FormikErrors<EditUserDetailsFormValues> = {};
  if (!values.phoneNumber || values.phoneNumber.length < 4) {
    errors.phoneNumber = 'Phone number is required';
  }
  if (initialValues.emailAddress && !values.emailAddress) {
    errors.emailAddress = 'Email address is required';
  }
  if (values.isConcierge && values.conciergeGroups.length === 0) {
    errors.conciergeGroups = 'Concierge group is required';
  }
  return errors;
};

const EditUserDetailsModal: FunctionComponent<PropsWithChildren<EditUserDetailsModalProps>> = ({
  user,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const partners = useSelector((state: StoreType) => state.partners.partners);

  const { actions } = useActions();

  useMountEffect(() => {
    actions.partners.fetchAll();
  });

  const sortedPartners = sortBy(partners, 'name');

  const handleClose = (resetForm: () => void) => {
    setError(null);
    resetForm();
    onClose();
  };

  const handleFormSubmit = (
    values: EditUserDetailsFormValues,
    formikHelpers: FormikHelpers<EditUserDetailsFormValues>
  ) => {
    if (!user.gdusUser?.gdusUuid) {
      setError('Error updating user: missing GDUS UUID');
      return Promise.reject();
    }

    return userClient
      .updateUser({
        uuid: user.gdusUser.gdusUuid,
        phoneNumber: values.phoneNumber,
        emailAddress: values.emailAddress,
        conciergeGroupNames: values.isConcierge ? values.conciergeGroups : [],
      })
      .then((response) => {
        if (response.response.case !== 'successResponse') {
          const errorResponse = response.response.value;
          const unroutable: string[] = [];
          // Each failure is a typed reason. Route conflicts to their field and keep anything
          // unattributable (service-unavailable, unspecified) in the general error area.
          (errorResponse?.errors ?? []).forEach((updateUserError) => {
            const { field, message } = resolveUpdateUserError(updateUserError);
            if (field) {
              formikHelpers.setFieldError(field, message);
            } else {
              unroutable.push(message);
            }
          });
          setError(unroutable.length > 0 ? unroutable.join(' ') : null);
          actions.toast.showError('Error updating user');
          return;
        }
        return onSubmit().then(() => {
          handleClose(formikHelpers.resetForm);
          actions.toast.showInfo('Account updated');
        });
      })
      .catch((rejection) => {
        setError(formatRpcError(rejection, 'update the account'));
        actions.toast.showError('Error updating user');
      });
  };

  const initialConciergeGroups =
    user.tockPatron?.partners?.map((partner) => partner.name ?? '') ?? [];
  // GDUS stores a single primary identifier, so an email-signup account has no phone identifier at
  // all. Fall back to Resy (which owns identifier uniqueness and stores E.164) and then Tock, so the
  // operator edits the account's real phone instead of an empty required field. Because phone is a
  // required field, saving an untouched email-signup account re-sends this pre-filled number, which
  // backfills the missing GDUS phone identifier — the self-excluding Resy conflict tier ensures the
  // account's own number is never rejected as already-in-use (see UserAdministrationServiceUpdateUserTest).
  const initialValues = {
    phoneNumber:
      user.gdusUser?.phoneNumber ?? user.resyUser?.mobileNumber ?? user.tockPatron?.phone ?? '',
    emailAddress: user.gdusUser?.email,
    isConcierge: user.resyUser?.isConcierge ?? user.tockPatron?.canBookForGuest ?? false,
    conciergeGroups: initialConciergeGroups,
  };

  return (
    <Formik<EditUserDetailsFormValues>
      initialValues={initialValues}
      validate={(values) => validate(initialValues, values)}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleSubmit,
        dirty,
        isValid,
        isSubmitting,
        setFieldValue,
        setValues,
        touched,
        values,
        resetForm,
      }) => (
        <Form>
          <Modal
            id="edit-user-details-modal"
            open={isOpen}
            onClose={() => handleClose(resetForm)}
            size="md"
          >
            <Modal.Header title="Edit account" />
            <Modal.Body>
              <Grid gap={100} alignItems="center">
                <Grid item span={12}>
                  <Alert
                    color="warning"
                    message="Changes to the account will be applied to GDUS, Resy, and Tock."
                  />
                </Grid>
                <Grid item gap={100} span={12}>
                  <PhoneInput
                    id="phone-number"
                    name="phoneNumber"
                    label="Phone number"
                    value={values.phoneNumber}
                    onChange={(value) => setFieldValue('phoneNumber', value)}
                    fullWidth
                  />
                  {errors.phoneNumber && (
                    <Text variant="body" color="fgDangerBase">
                      {errors.phoneNumber}
                    </Text>
                  )}
                </Grid>
                <Grid item gap={100} span={12}>
                  <TextInput
                    id="email-address"
                    name="emailAddress"
                    label="Email address"
                    value={values.emailAddress}
                    onChange={(event) => setFieldValue('emailAddress', event.target.value)}
                    fullWidth
                  />
                  {errors.emailAddress && (
                    <Text variant="body" color="fgDangerBase">
                      {errors.emailAddress}
                    </Text>
                  )}
                </Grid>
                <Grid item span={3}>
                  <Checkbox
                    id="is-concierge-checkbox"
                    name="isConcierge"
                    checked={values.isConcierge}
                    value="isConcierge"
                    label="Is concierge"
                    onChange={(event) => {
                      const isConcierge = event.target.checked;
                      const conciergeGroups = isConcierge ? initialConciergeGroups : [];
                      setValues({
                        phoneNumber: values.phoneNumber,
                        emailAddress: values.emailAddress,
                        isConcierge,
                        conciergeGroups,
                      });
                    }}
                  />
                </Grid>
                {values.isConcierge && (
                  <Grid item span={9} gap={50}>
                    <TagPicker
                      id="conciergeGroups"
                      label="Concierge groups"
                      currentValue={values.conciergeGroups.toString()}
                      onChange={(event: { currentTarget: { value: string; checked: boolean } }) => {
                        const selectedGroups = event.currentTarget.checked
                          ? [...values.conciergeGroups, event.currentTarget.value]
                          : values.conciergeGroups.filter(
                              (group) => group !== event.currentTarget.value
                            );
                        setFieldValue('conciergeGroups', selectedGroups);
                      }}
                    >
                      {sortedPartners.map((partner) => (
                        <TagPicker.Tag
                          key={partner.id}
                          value={partner.name}
                          label={partner.name}
                          checked={values.conciergeGroups.includes(partner.name)}
                        />
                      ))}
                    </TagPicker>
                    {touched.conciergeGroups && errors.conciergeGroups && (
                      <Text variant="body" color="fgDangerBase">
                        {errors.conciergeGroups}
                      </Text>
                    )}
                  </Grid>
                )}
                {error && (
                  <Grid item span={12}>
                    <Text variant="body" color="fgDangerBase">
                      {error}
                    </Text>
                  </Grid>
                )}
              </Grid>
            </Modal.Body>
            <Modal.Footer
              actions={{
                primary: {
                  label: isSubmitting ? 'Saving...' : 'Save',
                  type: 'submit',
                  disabled: !dirty || !isValid || isSubmitting,
                  onClick: () => handleSubmit(),
                },
                cancel: {
                  label: 'Cancel',
                  onClick: () => handleClose(resetForm),
                },
              }}
            />
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default EditUserDetailsModal;
